/**
 * Composer "Add to link in bio" — pure model (lane E1).
 *
 * When a post goes to Instagram (feed / Reel — Stories never appear in the
 * page's grid) or TikTok, and the selected client has a PUBLISHED link-in-bio
 * page, the composer can attach one link to the post. After the post saves the
 * composer PUTs it to `/api/bio-pages/{pageId}/post-links/by-post/{postId}`
 * (one row per page + post; the server applies the same URL rules as every
 * other bio link and picks the position). It shows on the post's Instagram
 * grid tile once the post has published there.
 *
 * @module lib/composerBioLink
 */

import type { BioPage, ComposerPostLink } from "../api/bioPages";
import type { Platform } from "../types/social";
import { urlProblem } from "./bioModel";
import type { InstagramFormat } from "./platformOptions";
import type { ComposerIssue } from "./platformRules";

export interface ComposerBioLink {
  enabled: boolean;
  /** The bio page the link goes on (a client can have several). */
  pageId: string | null;
  url: string;
  label: string;
}

export const DEFAULT_BIO_LINK: ComposerBioLink = { enabled: false, pageId: null, url: "", label: "" };

export const BIO_LINK_LABEL_MAX = 120;

/** Does this post reach a bio page audience? Instagram feed/Reel, or TikTok. */
export function bioLinkApplies(platforms: readonly Platform[], instagramFormat: InstagramFormat): boolean {
  return (platforms.includes("instagram") && instagramFormat !== "story") || platforms.includes("tiktok");
}

/** TikTok-only: the page has no TikTok grid, so the link only shows if Instagram is added too. */
export function bioLinkTiktokOnly(platforms: readonly Platform[], instagramFormat: InstagramFormat): boolean {
  return platforms.includes("tiktok") && !(platforms.includes("instagram") && instagramFormat !== "story");
}

/**
 * Pages the toggle can use: the client's PUBLISHED pages, plus any page that
 * already holds this post's link (so an edit can still move / remove it),
 * name-sorted by the API.
 */
export function bioLinkPages(
  pages: readonly BioPage[],
  brandId: string | null,
  existing: readonly ComposerPostLink[] = []
) {
  const keep = new Set(existing.map((l) => l.page_id));
  return pages.filter((p) => p.client_brand_id === brandId && (p.status === "published" || keep.has(p.id)));
}

/** The re-opened toggle state for a saved post (first saved link wins). */
export function bioLinkFromSaved(existing: readonly ComposerPostLink[]): ComposerBioLink {
  const first = existing[0];
  return first ? { enabled: true, pageId: first.page_id, url: first.url, label: first.label } : { ...DEFAULT_BIO_LINK };
}

/** Pre-publish-check issue for an enabled link (null = fine / not applicable). */
export function bioLinkIssue(
  link: ComposerBioLink,
  active: boolean,
  platforms: readonly Platform[] = ["instagram"]
): ComposerIssue | null {
  if (!active || !link.enabled) return null;
  const problem = !link.pageId ? "Choose the link-in-bio page for this post's link." : urlProblem(link.url);
  const platform: Platform = platforms.includes("instagram") ? "instagram" : "tiktok";
  return problem ? { platform, severity: "error", code: "bio_link", message: `Link in bio: ${problem}` } : null;
}

export interface BioLinkSyncPlan {
  /** Create / update the link on this page. */
  put: { pageId: string; url: string; label: string } | null;
  /** Pages whose link for this post must be removed. */
  remove: string[];
}

/**
 * What to write after the post saves. `active` = the toggle is shown (the
 * post targets IG feed / TikTok and the client has a page); `knownPageIds` =
 * pages that hold (or may hold) this post's link — loaded in edit mode or
 * written earlier in this session.
 */
export function bioLinkSyncPlan(
  link: ComposerBioLink,
  active: boolean,
  knownPageIds: Iterable<string>
): BioLinkSyncPlan {
  const want = active && link.enabled && link.pageId ? link.pageId : null;
  const remove = [...new Set(knownPageIds)].filter((id) => id !== want);
  return {
    put: want ? { pageId: want, url: link.url.trim(), label: link.label.trim().slice(0, BIO_LINK_LABEL_MAX) } : null,
    remove,
  };
}
